import Request from '../Request';

export type TopSellersView = 'total_store' | 'by_class' | 'by_product' | 'by_sku';
export type TopSellersHierarchy = 'total' | 'category' | 'class' | 'vendor' | 'location';
export type RankByMetric = 'sales_dollars' | 'sales_units' | 'gross_margin_pct' | 'gmroi';
export type SearchFieldMode = 'product_name' | 'tag' | 'sku';

// One SKU/variant nested under its parent product row in the "By SKU" view -- see
// TopSellersService::mergeVariantRow() for the authoritative field list. Only ever populated when
// view=by_sku; every other view's rows have an empty array here.
export interface SkuVariantApiRow {
  variant_id?: string | null;
  // Display-only; blank or null for variants without a SKU.
  sku_id: string | null;
  color: string | null;
  size: string | null;
  sales_units: number;
  sales_dollars: number;
  avg_selling_price: number | null;
  retail_price: number | null;
  markdown_dollars: number;
  gross_margin_pct: number | null; // 0-1 fraction, NOT 0-100
  gmroi: number | null;
  gmroi_tier: string | null;
  cash_margin_dollars: number | null;
  cash_margin_pct: number | null; // 0-1 fraction, NOT 0-100
  cmroi: number | null;
  inventory_oh_units: number;
  inventory_oh_dollars: number;
  weeks_of_supply: number | null;
  // Suggested Reorder inputs; the suggestion itself is computed client-side from the weeks target.
  avg_weekly_units: number | null;
  // Weekly units on full-price lines only (no discount, and no hard markdown where the client counts them).
  avg_weekly_units_full_price?: number | null;
  on_order_units: number;
  // Open PO lines at retail / at cost; cost is null when a line has no buy price.
  on_order_retail?: number;
  on_order_cost?: number | null;
  // Earliest dated open PO line (yyyy-MM-dd); null when none is dated.
  next_arrival_date?: string | null;
  // Receipts across every location at retail / at cost, the inputs of imu_pct.
  imu_retail?: number;
  imu_cost?: number;
  imu_pct?: number | null; // 0-1 fraction; null without receipts
  purchases_cost_dollars: number;
  markdown_pct: number | null; // 0-1 fraction of net sales
  cost_of_purchase_pct: number | null; // 0-1 fraction of net sales
  inflow_units?: number;
  // Receipts in the period at retail; with purchases_cost_dollars, the IMU behind mmu_pct.
  inflow_dollars?: number;
  mmu_pct?: number | null; // 0-1 fraction; null without receipts
  sell_through_pct?: number | null; // 0-1 fraction
  sell_through_start_inv_pct?: number | null; // 0-1 fraction
}

// Raw API row shape returned by GET /api/reports/top-sellers -- see
// TopSellersService::mergeRow() for the authoritative field list.
export interface TopSellerApiRow {
  item_rank: number;
  // Item identity at SKU grain; null at product grain, where sku_id carries the product_id.
  variant_id?: string | null;
  sku_id: string;
  // Product by Location hierarchy only; null otherwise.
  location_code?: string | null;
  product_title: string;
  vendor: string;
  m1_class_name: string;
  m1_class_code: string | null;
  // The store-level class under m1_class_* (which is the plan class, the one OTB is planned at).
  leaf_class_name?: string | null;
  category: string;
  sales_units: number;
  sales_dollars: number;
  avg_selling_price: number | null;
  retail_price: number | null;
  color: string | null; // SKU-grain template only -- always null on the Product rollup
  size: string | null; // SKU-grain template only -- always null on the Product rollup
  markdown_dollars: number;
  gross_margin_pct: number | null; // 0-1 fraction, NOT 0-100
  gmroi: number | null;
  gmroi_tier: string | null; // placeholder tiers -- see TopSellersService::GMROI_TIERS
  cash_margin_dollars: number | null;
  cash_margin_pct: number | null; // 0-1 fraction, NOT 0-100 -- same convention as gross_margin_pct
  cmroi: number | null;
  inventory_oh_units: number;
  inventory_oh_dollars: number;
  weeks_of_supply: number | null;
  // Suggested Reorder inputs; the suggestion itself is computed client-side from the weeks target.
  avg_weekly_units: number | null;
  // Weekly units on full-price lines only (no discount, and no hard markdown where the client counts them).
  avg_weekly_units_full_price?: number | null;
  on_order_units: number;
  // Open PO lines at retail / at cost; cost is null when a line has no buy price.
  on_order_retail?: number;
  on_order_cost?: number | null;
  // Earliest dated open PO line (yyyy-MM-dd); null when none is dated.
  next_arrival_date?: string | null;
  // Receipts across every location at retail / at cost, the inputs of imu_pct.
  imu_retail?: number;
  imu_cost?: number;
  imu_pct?: number | null; // 0-1 fraction; null without receipts
  purchases_cost_dollars: number;
  inflow_units?: number;
  // Receipts in the period at retail; with purchases_cost_dollars, the IMU behind mmu_pct.
  inflow_dollars?: number;
  mmu_pct?: number | null; // 0-1 fraction; null without receipts
  markdown_pct: number | null; // 0-1 fraction of net sales
  cost_of_purchase_pct: number | null; // 0-1 fraction of net sales
  sales_dollars_ly?: number;
  sales_units_ly?: number;
  avg_weekly_units_full_price_ly?: number | null;
  sales_change_pct?: number | null;
  avg_selling_price_ly?: number | null;
  purchases_cost_dollars_ly?: number;
  markdown_dollars_ly?: number;
  gross_margin_pct_ly?: number | null;
  gmroi_ly?: number | null;
  inventory_oh_units_ly?: number | null;
  inventory_oh_dollars_ly?: number | null;
  markdown_pct_ly?: number | null;
  cost_of_purchase_pct_ly?: number | null;
  inflow_units_ly?: number;
  inflow_dollars_ly?: number;
  mmu_pct_ly?: number | null;
  sell_through_pct?: number | null; // 0-1 fraction
  sell_through_start_inv_pct?: number | null; // 0-1 fraction
  sell_through_pct_ly?: number | null;
  sell_through_start_inv_pct_ly?: number | null;
  variants: SkuVariantApiRow[];
}

// Plan figures for one group (or the whole report), from Reporting.ReportDataSource.
export interface OtbMetrics {
  otb_retail: number;
  otb_cost: number;
  plan_sales_retail: number;
  plan_sales_cost: number;
  plan_inflow_retail: number;
  plan_inflow_cost: number;
  on_order_retail: number;
  on_order_cost: number;
}

export interface TopSellersOtb {
  span: { snapshot_epoch_month: number; start_epoch_month: number; end_epoch_month: number };
  total: OtbMetrics;
  // Keyed by the group's category / class name, upper-cased and trimmed. Empty for Total by Product.
  groups: Record<string, OtbMetrics>;
}

export interface TopSellersApiResponse {
  rows: TopSellerApiRow[];
  // Null for Vendor by Product / Product by Location (no plan grain) or when no plan data matched.
  otb?: TopSellersOtb | null;
  generated_at: string;
  currency: string;
  // Present when a BigQuery query behind the report failed: a user-facing message saying what's
  // missing (no rows at all, or just the Last Year / variant detail).
  query_error?: string;
}

export interface GetTopSellersParams {
  clientId: string | number;
  from: string; // 'YYYY-MM-DD'
  to: string;
  view: TopSellersView;
  hierarchy?: TopSellersHierarchy; // by_product / by_sku only
  rankBy: RankByMetric;
  topN: number;
  locationCodes?: string[];
  classCodes?: string[];
  categoryIds?: string[];
  // "Exclude selected instead": the location / class / category / vendor list becomes NOT IN.
  excludeLocations?: boolean;
  excludeClasses?: boolean;
  excludeCategories?: boolean;
  excludeVendors?: boolean;
  vendorIds?: string[];
  channelIds?: string[];
  minSales?: number;
  includeLy?: boolean;
  searchField?: SearchFieldMode;
  search?: string;
  onOrderSpan?: string;
  onOrderStartDate?: string;
  onOrderEndDate?: string;
}

/**
 * Thin wrapper around GET /api/reports/top-sellers.
 */
function getTopSellers(params: GetTopSellersParams): Promise<TopSellersApiResponse> {
  const { clientId, from, to, view, hierarchy, rankBy, topN, locationCodes, classCodes, categoryIds, excludeLocations, excludeClasses, excludeCategories, excludeVendors, vendorIds, channelIds, minSales, includeLy, searchField, search, onOrderSpan, onOrderStartDate, onOrderEndDate } = params;

  return Request.get('/api/reports/top-sellers', {
    client_id: clientId,
    from,
    to,
    view,
    hierarchy,
    rank_by: rankBy,
    top_n: topN,
    location_ids: locationCodes?.length ? locationCodes : undefined,
    class_ids: classCodes?.length ? classCodes : undefined,
    category_ids: categoryIds?.length ? categoryIds : undefined,
    // Sent only with a list to exclude; an empty list filters nothing either way.
    exclude_locations: excludeLocations && locationCodes?.length ? 1 : undefined,
    exclude_classes: excludeClasses && classCodes?.length ? 1 : undefined,
    exclude_categories: excludeCategories && categoryIds?.length ? 1 : undefined,
    vendor_ids: vendorIds?.length ? vendorIds : undefined,
    exclude_vendors: excludeVendors && vendorIds?.length ? 1 : undefined,
    channel_ids: channelIds?.length ? channelIds : undefined,
    min_sales: minSales,
    include_ly: includeLy ? 1 : 0,
    search_field: search ? searchField : undefined,
    search: search || undefined,
    on_order_span: onOrderSpan || undefined,
    on_order_start_date: onOrderStartDate || undefined,
    on_order_end_date: onOrderEndDate || undefined,
  });
}

/**
 * GET /api/reports/top-sellers/channels -- client-scoped distinct sales_channel values. Can
 * legitimately come back empty (`sales_channel` is unconfirmed against live BigQuery).
 */
async function getChannelOptions(clientId: string | number): Promise<string[]> {
  const res = await Request.get('/api/reports/top-sellers/channels', { client_id: clientId });
  return res?.channels ?? [];
}

/**
 * GET /api/reports/top-sellers/vendors -- every vendor in the client's catalog, so the Vendor
 * filter offers the full list up front rather than only the vendors in the current Top N.
 */
async function getVendorOptions(clientId: string | number): Promise<string[]> {
  try {
    const res = await Request.get('/api/reports/top-sellers/vendors', { client_id: clientId });
    return res?.vendors ?? [];
  } catch (error) {
    console.error(error);
    return [];
  }
}

export interface DropdownOption {
  code: string;
  name: string;
  label?: string;
}

export interface DropdownOptions {
  locations: DropdownOption[];
  categories: DropdownOption[];
  classes: DropdownOption[];
}

/**
 * GET /api/clients/{clientId}/dropdown-options -- the existing shared location/category/class
 * filter-options endpoint, reused here. Returns CODE strings, not numeric ids.
 */
async function getDropdownOptions(clientId: string | number): Promise<DropdownOptions> {
  const res = await Request.get(`/api/clients/${clientId}/dropdown-options`, {});
  return {
    locations: res?.locations ?? [],
    categories: res?.categories ?? [],
    classes: res?.classes ?? [],
  };
}

const TopSellersApi = {
  getTopSellers,
  getChannelOptions,
  getVendorOptions,
  getDropdownOptions,
};

export default TopSellersApi;
